import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck, Copy, AlertTriangle, Loader2, KeyRound, Smartphone, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

/**
 * 2FA-enrollment ennen ensimmäistä kirjautumista.
 *  - Idempotentti: jos käyttäjällä on jo voimassa oleva pending_secret,
 *    backend palauttaa saman.
 *  - "Pyydä uusi QR-koodi" -nappi pakottaa backendin generoimaan uuden
 *    secretin jos käyttäjän Authenticator-sovelluksessa on vanha QR.
 */
export default function TwoFactorSetup({ setupToken, email, fullName, onCompleted, onCancel }) {
  const [stage, setStage] = useState("loading");
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [userPayload, setUserPayload] = useState(null);

  const load = async (regenerate = false) => {
    setError("");
    setEnroll(null);
    setCode("");
    setStage("loading");
    if (regenerate) setRegenerating(true);
    try {
      const r = await api.post("/auth/2fa/enroll-start", {
        setup_token: setupToken,
        regenerate,
      });
      setEnroll(r.data);
      setStage("scan");
      if (regenerate) toast.success("Uusi QR-koodi luotu – skannaa se Authenticator-sovellukseen");
    } catch (e) {
      setError(formatApiError(e));
      setStage("error");
    }
    setRegenerating(false);
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupToken]);

  const verify = async () => {
    setError("");
    const clean = code.replace(/\D/g, "");
    if (clean.length < 6) return setError("Syötä 6-numeroinen koodi");
    setVerifying(true);
    try {
      const r = await api.post("/auth/2fa/enroll-verify", {
        setup_token: setupToken,
        code: clean,
      });
      setRecoveryCodes(r.data.recovery_codes || []);
      setUserPayload(r.data);
      setStage("recovery");
    } catch (e) {
      setError(formatApiError(e));
    }
    setVerifying(false);
  };

  const copyRecovery = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"));
    toast.success("Varakoodit kopioitu");
  };

  const finish = () => onCompleted?.(userPayload);

  return (
    <div className="app-bg flex items-center justify-center px-4 py-12 min-h-screen relative" data-testid="twofa-setup">
      <div className="w-full max-w-xl glass-strong p-8 space-y-5 relative z-10 animate-fade-in">
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 via-amber-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-white" />
            <div className="absolute -inset-1 rounded-2xl bg-amber-400/30 blur-md -z-10" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Tietoturva</p>
            <h1 className="text-2xl font-display font-bold gradient-text-luxe leading-tight">Aktivoi 2FA</h1>
            <p className="text-xs text-white/50 truncate">{email} – {fullName}</p>
          </div>
        </div>

        <div className="rounded-xl border-2 border-red-400/40 bg-red-500/10 p-3 text-xs text-red-100 flex gap-2 leading-relaxed">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-300" />
          <p>
            <strong className="text-red-200">TÄRKEÄÄ ennen kuin skannaat:</strong> Jos Authenticator-sovelluksessasi on
            jo "Apps2Go Hub" -merkintä (esim. aiemmasta yrityksestä), <strong>poista se ensin</strong> –
            muuten sovellus näyttää väärän koodin samalla nimellä. Pidä vain UUSI merkintä.
          </p>
        </div>

        <div className="rounded-xl border border-amber-400/20 bg-amber-400/5 p-3 text-xs text-amber-100 flex gap-2 leading-relaxed">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-300" />
          <p>
            Pääkäyttäjä on määrännyt kaksivaiheisen tunnistautumisen <strong>pakolliseksi</strong>.
            Skannaa QR-koodi <strong>Google Authenticator</strong>, <strong>Authy</strong> tai vastaavalla sovelluksella ja vahvista 6-numeroisella koodilla.
          </p>
        </div>

        {stage === "loading" && (
          <div className="flex items-center justify-center py-12 text-white/50 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Ladataan...
          </div>
        )}

        {stage === "error" && (
          <div className="text-center space-y-3 py-6">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" onClick={onCancel}>Takaisin kirjautumiseen</Button>
          </div>
        )}

        {(stage === "scan" || stage === "verify") && enroll && (
          <div className="space-y-4">
            <div className="relative bg-white rounded-2xl p-4 mx-auto w-fit shadow-2xl shadow-amber-500/20">
              <img src={enroll.qr_data_url} alt="2FA QR-koodi" className="w-56 h-56 block" data-testid="twofa-qr" />
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-amber-400 to-purple-600 opacity-20 blur-md -z-10" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Tai syötä avain käsin</p>
              <div className="bg-black/40 rounded-lg px-3 py-3 border border-white/5" data-testid="twofa-secret-wrap">
                <p
                  className="font-mono text-sm tracking-wider text-amber-200"
                  style={{ fontVariantNumeric: "slashed-zero tabular-nums", fontFamily: "ui-monospace, 'SF Mono', 'Cascadia Mono', 'Roboto Mono', monospace" }}
                >
                  {(enroll.secret.match(/.{1,4}/g) || []).join(" ")}
                </p>
                <p
                  className="font-mono text-[10px] mt-2 text-white/40 break-all"
                  data-testid="twofa-secret"
                  style={{ fontVariantNumeric: "slashed-zero" }}
                >
                  {enroll.secret}
                </p>
              </div>
              <p className="text-[10px] text-amber-200/70 px-2">
                💡 Avain sisältää vain kirjaimia A–Z (ei O, ei I) ja numeroita 2–7. Älä sekoita kirjaimia numeroihin.
              </p>
              <div className="flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(enroll.secret);
                    toast.success("Avain kopioitu");
                  }}
                  className="text-xs gap-1 text-white/60 hover:text-white"
                >
                  <Copy className="w-3 h-3" /> Kopioi avain
                </Button>
                <span className="text-white/20">·</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => load(true)}
                  disabled={regenerating}
                  className="text-xs gap-1 text-amber-300 hover:text-amber-200"
                  data-testid="twofa-regenerate"
                  title="Generoi uusi QR-koodi"
                >
                  {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Pyydä uusi QR-koodi
                </Button>
              </div>
            </div>

            <div className="border-t border-white/10 pt-4 space-y-3">
              <Label className="text-sm flex items-center gap-2 text-white/80">
                <Smartphone className="w-4 h-4 text-amber-300" />
                Syötä sovelluksen 6-numeroinen koodi
              </Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123 456"
                maxLength={10}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className="text-center text-xl font-mono tracking-[0.5em] bg-black/30 border-white/10 h-12"
                data-testid="twofa-code"
              />
              {error && (
                <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-200 leading-relaxed">
                  {error}
                </div>
              )}
              <Button
                onClick={verify}
                disabled={verifying}
                className="btn-luxe w-full inline-flex items-center justify-center gap-2"
                data-testid="twofa-verify"
              >
                {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Vahvista ja aktivoi
              </Button>
              <Button variant="ghost" size="sm" onClick={onCancel} className="w-full text-xs text-white/40 hover:text-white/70">
                Peruuta ja palaa kirjautumiseen
              </Button>
            </div>
          </div>
        )}

        {stage === "recovery" && (
          <div className="space-y-4">
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/30 p-3 text-xs text-emerald-100 flex gap-2 leading-relaxed">
              <KeyRound className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-300" />
              <p>
                <strong>Tallenna nämä varakoodit!</strong> Jokaista voi käyttää kerran, jos puhelimesi katoaa.
                Säilytä turvallisessa paikassa – näytetään vain kerran.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm font-mono bg-black/40 p-4 rounded-xl border border-white/5" data-testid="twofa-recovery-codes">
              {recoveryCodes.map((c) => (
                <div key={c} className="text-amber-300">{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={copyRecovery} className="btn-luxe flex-1 inline-flex items-center justify-center gap-2">
                <Copy className="w-4 h-4" /> Kopioi koodit
              </Button>
              <Button variant="outline" onClick={finish} className="flex-1 border-white/10 text-white hover:bg-white/5" data-testid="twofa-finish">
                Tallennettu – jatka
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
